import { GRAVITY, OVERHANG_THICK, REPOSE, SLIDE_MARGIN, SLIDE_MAX_MOVES, SLIDE_PASSES, SLIDE_UP, WATER_Y, WORLD_H, WORLD_W } from './constants';
import { mulberry32 } from './rng';
import { WORLDS } from './worlds';

export type TerrainStyle = 'hills' | 'caves' | 'moon' | 'mars';

/**
 * [druh, x, y, r] – vždy celá čísla, aby rekonstrukce byla přesná.
 * Druh: 0 = vyhloubit kruh, 1 = nasypat kruh, 2 = sesuv půdy v okolí kruhu.
 */
export type TerrainOp = [kind: 0 | 1 | 2, x: number, y: number, r: number];

export function makeOp(kind: 0 | 1 | 2, x: number, y: number, r: number): TerrainOp {
  return [kind, Math.round(x), Math.round(y), Math.round(r)];
}

export class Terrain {
  readonly data: Uint8Array;
  /** Gravitace světa (px/s²) – Měsíc a Mars mají slabší. */
  gravity = GRAVITY;

  constructor(
    readonly w: number,
    readonly h: number,
    data?: Uint8Array,
  ) {
    this.data = data ?? new Uint8Array(w * h);
  }

  solid(x: number, y: number): boolean {
    const ix = Math.floor(x);
    const iy = Math.floor(y);
    if (ix < 0 || ix >= this.w || iy < 0 || iy >= this.h) return false;
    return this.data[iy * this.w + ix] === 1;
  }

  /** Nejvyšší pevný pixel ve sloupci, případně `h`, pokud je sloupec prázdný. */
  surfaceY(x: number): number {
    const ix = Math.floor(x);
    if (ix < 0 || ix >= this.w) return this.h;
    for (let y = 0; y < this.h; y++) if (this.data[y * this.w + ix] === 1) return y;
    return this.h;
  }

  /** Aplikuje op; u sesuvu vrací přesuny zrnek [fx, fy, tx, ty, …]. */
  apply(op: TerrainOp): number[] | undefined {
    if (op[0] === 2) return this.landslide(op[1], op[2], op[3]);
    this.circle(op[1], op[2], op[3], op[0]);
    return undefined;
  }

  /**
   * Udrží se zrnko (x, y), pod kterým je prázdno? Jen když je součástí silné
   * vrstvy – aspoň OVERHANG_THICK px souvisle nad ním (klenba, strop jeskyně).
   * Tenké převisy a strop kráteru těsně pod povrchem se propadnou.
   */
  private holds(x: number, y: number): boolean {
    const { w, data: d } = this;
    let thick = 1;
    while (thick < OVERHANG_THICK && y - thick >= 0 && d[(y - thick) * w + x]) thick++;
    return thick >= OVERHANG_THICK;
  }

  /**
   * Sesuv půdy kolem kráteru (cx, cy, r). Funguje jako písek: povrchová zrnka
   * sklouznou do sousedního sloupce, pokud by tam dopadla o víc než REPOSE px níž,
   * a zrnka bez jakékoli opory spadnou svisle. Jen celá čísla a pevné pořadí
   * průchodu → server i všechny prohlížeče dojdou k identickému výsledku.
   */
  landslide(cx: number, cy: number, r: number): number[] {
    const { w, h, data: d } = this;
    const x0 = Math.max(1, cx - r - SLIDE_MARGIN);
    const x1 = Math.min(w - 2, cx + r + SLIDE_MARGIN);
    const y0 = Math.max(1, cy - r - SLIDE_UP);
    const y1 = Math.min(h - 2, cy + r + SLIDE_MARGIN);
    const moves: number[] = [];
    if (x1 < x0 || y1 < y0) return moves;

    // Kam dopadne zrnko puštěné z prázdné buňky (x, y)
    const land = (x: number, y: number) => {
      let ny = y;
      while (ny + 1 < h && d[(ny + 1) * w + x] === 0) ny++;
      return ny;
    };

    for (let pass = 0; pass < SLIDE_PASSES && moves.length < SLIDE_MAX_MOVES * 4; pass++) {
      let moved = false;
      const leftToRight = pass % 2 === 0;
      // Shora dolů: když sklouzne horní zrnko stěny, to pod ním se tím stane
      // povrchovým a může sklouznout ve stejném průchodu → stěna se sesype celá
      for (let y = y0; y <= y1; y++) {
        const row = y * w;
        for (let i = 0; i <= x1 - x0; i++) {
          const x = leftToRight ? x0 + i : x1 - i;
          const k = row + x;
          if (d[k] === 0) continue;
          const above = d[k - w];
          if (!d[k + w] && !this.holds(x, y)) {
            // Tenký převis bez opory → zrnko spadne svisle (další nad ním
            // se zpracují hned po něm, takže celý sloupec převisu padá naráz)
            d[k] = 0;
            const ty = land(x, y);
            d[ty * w + x] = 1;
            moves.push(x, y, x, ty);
            moved = true;
            continue;
          }
          if (above) continue; // není na povrchu
          let dir = 0;
          let bestY = -1;
          // Rozdíl povrchů = propad + 1 → drží se rozdíl nejvýš REPOSE
          let bestDrop = REPOSE - 1;
          for (const s of [-1, 1]) {
            if (d[k + s]) continue;
            const ty = land(x + s, y);
            if (ty - y > bestDrop) {
              bestDrop = ty - y;
              dir = s;
              bestY = ty;
            }
          }
          if (dir) {
            // Zrnko se kutálí dál stejným směrem, dokud je svah pod ním strmý
            let tx = x + dir;
            let ty = bestY;
            for (let roll = 0; roll < SLIDE_MARGIN * 2; roll++) {
              const nx = tx + dir;
              if (nx < 1 || nx > w - 2 || d[ty * w + nx]) break;
              const ny = land(nx, ty);
              if (ny - ty < REPOSE) break;
              tx = nx;
              ty = ny;
            }
            d[k] = 0;
            d[ty * w + tx] = 1;
            moves.push(x, y, tx, ty);
            moved = true;
          }
        }
      }
      if (!moved) break;
    }
    return moves;
  }

  private circle(cx: number, cy: number, r: number, value: number): void {
    for (let dy = -r; dy <= r; dy++) {
      const y = cy + dy;
      if (y < 0 || y >= this.h) continue;
      const dx = Math.floor(Math.sqrt(r * r - dy * dy));
      const x0 = Math.max(0, cx - dx);
      const x1 = Math.min(this.w - 1, cx + dx);
      const row = y * this.w;
      for (let x = x0; x <= x1; x++) this.data[row + x] = value;
    }
  }

  clone(): Terrain {
    const t = new Terrain(this.w, this.h, this.data.slice());
    t.gravity = this.gravity;
    return t;
  }
}

/**
 * Deterministická generace terénu ze seedu. Používá jen +, −, ×, ÷ a sqrt
 * (IEEE přesně zaokrouhlené), takže server i všechny prohlížeče dostanou identickou mapu.
 */
export function generateTerrain(seed: number, style: TerrainStyle): Terrain {
  const rng = mulberry32(seed);
  const t = new Terrain(WORLD_W, WORLD_H);
  t.gravity = GRAVITY * WORLDS[style].gravity;

  // Midpoint displacement
  const N = 1024;
  const pts = new Float64Array(N + 1);
  pts[0] = rng() * 2 - 1;
  pts[N] = rng() * 2 - 1;
  let amp = 1;
  const rough = style === 'caves' ? 0.5 : style === 'moon' ? 0.48 : style === 'mars' ? 0.6 : 0.56;
  for (let step = N; step > 1; step /= 2) {
    const half = step / 2;
    for (let i = half; i < N; i += step) {
      pts[i] = (pts[i - half] + pts[i + half]) / 2 + (rng() * 2 - 1) * amp;
    }
    amp *= rough;
  }
  let min = Infinity;
  let max = -Infinity;
  for (const p of pts) {
    if (p < min) min = p;
    if (p > max) max = p;
  }
  const span = max - min || 1;
  const top = WORLD_H * (style === 'caves' ? 0.22 : style === 'moon' ? 0.4 : 0.3);
  const bottom = WORLD_H * (style === 'caves' ? 0.62 : style === 'moon' ? 0.74 : 0.8);
  if (style === 'mars') {
    // Stolové hory: výšky se zčásti „srovnají“ do teras
    for (let i = 0; i <= N; i++) {
      const terrace = Math.round(pts[i] * 4) / 4;
      pts[i] = terrace * 0.75 + pts[i] * 0.25;
    }
  }

  const surface = new Int32Array(WORLD_W);
  for (let x = 0; x < WORLD_W; x++) {
    const f = (x * N) / (WORLD_W - 1);
    const i = Math.min(Math.floor(f), N - 1);
    const frac = f - i;
    const p = pts[i] * (1 - frac) + pts[i + 1] * frac;
    surface[x] = Math.floor(top + ((p - min) / span) * (bottom - top));
  }
  for (let x = 0; x < WORLD_W; x++) {
    for (let y = surface[x]; y < WORLD_H; y++) t.data[y * WORLD_W + x] = 1;
  }

  if (style === 'moon') {
    // Krátery: vyhloubený kruh a kolem něj navršený val
    const craters = 7 + Math.floor(rng() * 5);
    for (let k = 0; k < craters; k++) {
      const x = 60 + rng() * (WORLD_W - 120);
      const r = 18 + rng() * 55;
      const sx = Math.min(WORLD_W - 1, Math.max(0, Math.floor(x)));
      const y = surface[sx] + r * 0.35;
      t.apply(makeOp(1, x - r * 0.95, y - r * 0.3, r * 0.32));
      t.apply(makeOp(1, x + r * 0.95, y - r * 0.3, r * 0.32));
      t.apply(makeOp(0, x, y, r));
    }
  }

  // Jeskyně: náhodné procházky vyhlubující kruhy
  const tunnels = style === 'caves' ? 4 + Math.floor(rng() * 3) : style === 'moon' ? 0 : Math.floor(rng() * 2);
  for (let k = 0; k < tunnels; k++) {
    let x = 80 + rng() * (WORLD_W - 160);
    const sx = Math.min(WORLD_W - 1, Math.max(0, Math.floor(x)));
    let y = surface[sx] + 70 + rng() * 160;
    let dx = rng() * 2 - 1;
    let dy = (rng() * 2 - 1) * 0.3;
    const len = 18 + Math.floor(rng() * 26);
    for (let s = 0; s < len; s++) {
      const r = Math.round(16 + rng() * 16);
      if (y > WATER_Y - 50) dy = -Math.abs(dy) - 0.2;
      const cx = Math.floor(x);
      const surf = surface[Math.min(WORLD_W - 1, Math.max(0, cx))];
      if (y < surf + 40) dy = Math.abs(dy) + 0.2;
      t.apply(makeOp(0, x, y, r));
      dx += (rng() - 0.5) * 0.6;
      dy += (rng() - 0.5) * 0.6;
      dy *= 0.85;
      const n = Math.sqrt(dx * dx + dy * dy) || 1;
      dx /= n;
      dy /= n;
      x += dx * 13;
      y += dy * 13;
      if (x < 20 || x > WORLD_W - 20) break;
    }
  }
  return t;
}

export function buildTerrain(seed: number, style: TerrainStyle, ops: readonly TerrainOp[]): Terrain {
  const t = generateTerrain(seed, style);
  for (const op of ops) t.apply(op);
  return t;
}
